import React, { forwardRef } from 'react';

import { Icon, Loader, Popup } from 'ui';

type Props = {
  form?: string;
  size?: 'tiny' | 'small' | 'medium' | 'large' | 'huge';
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  negative?: boolean;
  grayLink?: boolean;
  link?: boolean;
  icon?: React.ComponentProps<typeof Icon>['name'];
  iconSolid?: React.ComponentProps<typeof Icon>['solid'];
  iconSize?: React.ComponentProps<typeof Icon>['size'];
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  disabled?: boolean;
  fluid?: boolean;
  submit?: boolean;
  title?: string;
  id?: string;
  attached?: 'left' | 'both' | 'right';
  classNames?: (classNames: string[]) => string[];
  children?: React.ReactNode | string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
  wrap?: boolean;
};

const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      form,
      size,
      primary,
      secondary,
      tertiary,
      negative,
      grayLink,
      link,
      icon,
      iconSolid,
      iconSize,
      iconPosition = 'left',
      loading,
      disabled,
      fluid,
      id,
      submit = false,
      title,
      attached,
      classNames = (classes: string[]) => classes,
      children,
      onClick,
      onMouseEnter,
      onMouseLeave,
      wrap = false,
    }: Props,
    ref,
  ) => {
    if (!iconSize) {
      iconSize = size;
    }

    if (loading) disabled = true;

    const classArray = [
      'inline-flex',
      'items-center',
      'font-medium',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-2',
      'focus:ring-primary-500',
    ];
    let iconClassName = '';

    if (!wrap) {
      classArray.push('whitespace-nowrap');
    }

    if (!link) {
      let roundingSize = '';

      switch (size) {
        case 'tiny':
          classArray.push('px-2.5', 'py-1.5', 'text-xs');
          if (children) {
            iconClassName = '-ml-0.5 mr-1 h-4 w-4';
          }
          break;
        case 'small':
          roundingSize = '-md';
          classArray.push('px-3', 'py-2', 'text-sm');
          if (children) {
            iconClassName = '-ml-0.5 mr-1.5 h-4 w-4';
          }
          break;
        default:
        case 'medium':
          roundingSize = '-md';
          if (attached) {
            classArray.push('px-4', 'py-2', 'text-base', 'sm:text-sm');
          } else {
            classArray.push('px-4', 'py-2', 'text-sm');
          }
          if (children) {
            iconClassName = '-ml-1 mr-2 h-5 w-5';
          }
          break;
        case 'large':
          roundingSize = '-md';
          classArray.push('px-4', 'py-2', 'text-base');
          if (children) {
            iconClassName = '-ml-1 mr-2 h-5 w-5';
          }
          break;
        case 'huge':
          roundingSize = '-md';
          classArray.push('px-6', 'py-3', 'text-base');
          if (children) {
            iconClassName = '-ml-1 mr-3 h-6 w-6';
          }
          break;
      }

      switch (attached) {
        case 'left':
          classArray.push(`rounded-r${roundingSize}`);
          break;
        case 'right':
          classArray.push(`rounded-l${roundingSize}`);
          break;
        case 'both':
          classArray.push('rounded-none');
          break;
        default:
          classArray.push(`rounded${roundingSize}`);
          break;
      }
    }

    if (primary) {
      classArray.push(
        'text-white',
        'bg-primary-600',
        'hover:bg-primary-700',
        'border',
        'border-transparent',
        'shadow-sm',
      );
    } else if (secondary) {
      classArray.push('text-primary-700', 'bg-primary-100', 'hover:bg-primary-200', 'border', 'border-transparent');
    } else if (tertiary) {
      classArray.push('text-primary-600', 'hover:text-primary-800');
    } else if (link && negative) {
      classArray.push('text-red-600', 'hover:text-red-800', 'text-sm');
    } else if (grayLink) {
      classArray.push('text-gray-400', 'hover:text-gray-700', 'text-sm');
    } else if (link) {
      classArray.push('text-primary-600', 'hover:text-primary-800', 'text-sm');
    } else if (negative) {
      classArray.push('text-white', 'bg-red-600', 'hover:bg-red-700', 'border', 'border-transparent', 'shadow-sm');
    } else {
      classArray.push('text-gray-700', 'bg-white', 'hover:bg-gray-50', 'border', 'border-gray-300', 'shadow-sm');
    }

    if (loading) {
      classArray.push('cursor-wait');
    } else if (disabled) {
      classArray.push('cursor-not-allowed');
    }

    if (disabled) {
      classArray.push('opacity-50');
    }

    if (fluid) {
      classArray.push('w-full', 'justify-center');
    }

    return (
      <button
        form={form}
        id={id}
        ref={ref}
        type={submit ? 'submit' : 'button'}
        title={title}
        disabled={disabled}
        className={classNames(classArray).join(' ')}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {!loading && icon && iconPosition === 'left' && (
          <Icon
            solid={iconSolid}
            name={icon}
            size={iconSize}
            className={iconClassName}
            fitted={!React.Children.count(children)}
          />
        )}
        {loading && <Loader />}
        <div className="contents">{children}</div>
        {!loading && icon && iconPosition === 'right' && (
          <span className="ml-3.5 -mr-4">
            <Icon solid={iconSolid} fitted name={icon} size={iconSize} className={iconClassName} />
          </span>
        )}
      </button>
    );
  },
);

export default Button;
